import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Button
} from 'reactstrap';
import axios from 'axios';
import api from '../../config.js';
import aboutImage from '../assets/images/about-apexcxos.png';

const AboutUs = () => {
  // State management
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch About Us data from CMS
  const fetchAboutData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get('/api/public/cms/about-us');
      
      if (response.data.isOk) {
        setAboutData(response.data.data);
      } else {
        setError('Failed to fetch about us data');
      }
    } catch (error) {
      console.error('Error fetching about us data:', error);
      setError('Error loading about us content');
      setAboutData(null); // Fallback to static content
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchAboutData();
  }, []);

  // Loading state
  if (loading) {
    return (
      <section className="about-section">
        <Container>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading about us...</span>
            </div>
          </div>
        </Container>
      </section>
    );
  }

  // Get current data (dynamic if available, fallback to static)
  const currentData = aboutData || {
    heading1: "Built for Leaders.",
    heading2: "Backed by Community.",
    heading3: "Designed for Impact.",
    description1: "At ApexCXOs, we are more than a network. We are a trusted community for CISOs, CIOs, CTOs, CDOs, and other C-suite executives driving cybersecurity, data, and digital transformation. Our vision is to empower leaders with meaningful peer connections, valuable insights, and actionable strategies that deliver resilience, innovation, and growth.",
    description2: "Through high-impact events, peer-led forums, and trusted knowledge exchange, ApexCXOs equips executives to lead with clarity and shape the future of enterprise security and technology."
  };

  return (
    <section className="about-section">
      <Container>
        <Row className="align-items-center">
          
          <Col lg={6} className="mb-4 mb-lg-0">
            <p className="text-uppercase small mb-2 about-subtitle">About Us</p>
            <h2 className="mb-3 about-title">
              {currentData.heading1 && (
                <>
                  {currentData.heading1}<br />
                </>
              )}
              {currentData.heading2 && (
                <>
                  {currentData.heading2}<br />
                </>
              )}
              {currentData.heading3 && currentData.heading3}
            </h2>
            <div className="about-text display-4 mb-4">
              {currentData.description1 && (
                <p>{currentData.description1}</p>
              )}
              {currentData.description2 && (
                <p>{currentData.description2}</p>
              )}
              
              {/* Display points if available */}
              {currentData.points && currentData.points.length > 0 && (
                <div className="about-points mt-3">
                  {currentData.points.map((point, index) => (
                    <div key={index} className="mb-3">
                      {point.subHeading && (
                        <h5 className="fw-bold">{point.subHeading}</h5>
                      )}
                      {point.description && (
                        <p>{point.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button 
              tag="a" 
              href="/community" 
              className="btn-connect"
            >
              CONNECT WITH US
            </Button>
          </Col>

          <Col lg={6}>
            <img 
              src={
                currentData.image 
                  ? `${api.API_URL}/${currentData.image}` 
                  : aboutImage
              } 
              alt="About ApexCXOs" 
              className="img-fluid rounded"
              onError={(e) => {
                // Fallback to static image if dynamic image fails to load
                e.target.src = aboutImage;
              }}
            />
          </Col>

        </Row>
        
        {/* Show error message if data failed to load but don't break the UI */}
        {error && (
          <Row className="mt-3">
            <Col xs="12">
              <div className="alert alert-warning text-center" role="alert">
                <small>{error} - Displaying default content</small>
              </div>
            </Col>
          </Row>
        )}
      </Container>
    </section>
  );
};

export default AboutUs;
